import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { QUERY_GET_UNIT_GENERIC_DETAILS } from '@apps-common/apis/portal/unit'
import {
  ModifyUnitActivationStep,
  stepDirection
} from '@apps-common/apps/portal/pages/units/pages/activations/pages/activation-modify/Index'
import { ImageComponent } from '@apps-common/components/common'
import { ButtonCopy, ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { FieldErrorMessage } from '@apps-common/components/elements/form'
import { CollapsibleContent, VertSpacerLg } from '@apps-common/components/layout'
import { ModalButton } from '@apps-common/components/modals'
import { INFO_ITEM_SERVICE_REQUESTS, InfoModalButton } from '@apps-common/components/modals/info-modal'
import { PortalUnitSelectModal } from '@apps-common/components/modals/PortalUnitSelectModal'
import { commonIcons } from '@apps-common/config/icons'
import { PORTAL_UNIT_FILER_DEFAULTS_ACTIVE, PORTAL_UNIT_FILER_DEFAULTS_INACTIVE } from '@apps-common/config/unit-filter-defaults'
import { UnitPlanUpgradeability } from '@apps-common/config/unit-plan'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { queryClient } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IActivationServiceRequestCreateModel } from '@apps-common/types/portal/service-request'
import { IPortalUnitSearchModel, IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'
import { ROUTE_UNIT_ACTIVATION_DETAILS } from '@apps-radionet-portal-root/src/config/private-routes'

interface IProps {
  loading: boolean
  updating: boolean
  step: ModifyUnitActivationStep
  moveStep: (direction: stepDirection) => void
  defaultUnit?: IUnitGenericDetailsModel
}

export const UnitActivationUnitForm = ({ loading, updating, step, moveStep, defaultUnit }: IProps) => {
  const { navigateNewTab } = useNavigation()
  const { register, setValue, watch, formState } = useFormContext<IActivationServiceRequestCreateModel>()
  const { errors } = formState

  const [existingUnit, setExistingUnit] = useState<IPortalUnitSearchModel>()

  const unitIdVal = watch('unitId')
  const existingUnitIdVal = watch('existingUnitId')
  const serialNoVal = watch('serialNo')
  const imeiVal = watch('imei')
  const productImageUrlVal = watch('productImageUrl')
  const productNameVal = watch('productName')

  const updateValues = (
    unitId: string,
    serialNo: string,
    imei: string,
    productImageUrl: string,
    productId: string,
    productName: string,
    planId: string,
    planName: string,
    planUpgradePath: string
  ) => {
    setValue('unitId', unitId, { shouldValidate: true })
    setValue('serialNo', serialNo, { shouldValidate: true })
    setValue('imei', imei, { shouldValidate: true })
    setValue('productImageUrl', productImageUrl, { shouldValidate: true })
    setValue('productId', productId, { shouldValidate: true })
    setValue('productName', productName, { shouldValidate: true })
    setValue('unitPlanUpgradePath', planUpgradePath, { shouldValidate: true })

    if (planUpgradePath === UnitPlanUpgradeability.fixed) {
      setValue('unitPlanId', planId, { shouldValidate: true })
      setValue('unitPlanName', planName, { shouldValidate: true })
    }
  }

  const clearValues = () => {
    setValue('unitId', '')
    setValue('serialNo', '')
    setValue('imei', '')
    setValue('productImageUrl', '')
    setValue('productId', '')
    setValue('unitPlanId', '')
    setValue('unitPlanName', '')
    setValue('unitPlanUpgradePath', '')

    queryClient.resetQueries({ queryKey: QUERY_GET_UNIT_GENERIC_DETAILS(unitIdVal) })
  }

  useEffect(() => {
    // set other existing unit
    setValue('mCentreId', existingUnit?.monitoringCentreId || '', { shouldValidate: true })
    setValue('mCentre', existingUnit?.monitoringCentre || '', { shouldValidate: true })

    setValue('bureauId', existingUnit?.bureauId || '', { shouldValidate: true })
    setValue('bureau', existingUnit?.bureau || '', { shouldValidate: true })

    setValue('techId', existingUnit?.techId || '', { shouldValidate: true })
    setValue('tech', existingUnit?.tech || '', { shouldValidate: true })

    // set existing unit values
    setValue('existingUnitId', existingUnit?.unitId, { shouldValidate: true })
    setValue('existingUnitProductImageUrl', existingUnit?.productImageUrl || '', { shouldValidate: true })
    setValue('existingUnitSiteName', existingUnit?.siteName || '', { shouldValidate: true })
    setValue('existingUnitAccountNo', existingUnit?.accountNo || '', { shouldValidate: true })
    setValue('existingUnitImei', existingUnit?.imei || '', { shouldValidate: true })
    setValue('existingUnitSerialNo', existingUnit?.serialNo || '', { shouldValidate: true })
    setValue('existingUnitMobileNo', existingUnit?.mobileNo || '', { shouldValidate: true })

    // set account no of activation to existing unit account
    setValue('accountNo', existingUnit?.accountNo || '', { shouldValidate: true })
  }, [existingUnit])

  useEffect(() => {
    if (defaultUnit) {
      const { unitId, serialNo, imei, imageUrl, productId, product, planId, planName, planUpgradePath } = defaultUnit

      updateValues(unitId, serialNo, imei, imageUrl, productId, product, planId, planName, planUpgradePath)
    }
  }, [defaultUnit])

  useEffect(() => {
    register('unitId', {
      validate: (value) => {
        if (step !== ModifyUnitActivationStep.unit) {
          return true
        }

        if (!value || value.length === 0) {
          return 'Inactive device required'
        }

        return true
      }
    })

    register('existingUnitId', {
      validate: (value) => {
        if (step !== ModifyUnitActivationStep.unit) {
          return true
        }

        if (!value || value.length === 0) {
          return 'Existing device required'
        }

        return true
      }
    })
  }, [])

  return (
    <CollapsibleContent
      title='Step 1 | Device'
      defaultOpen={step === ModifyUnitActivationStep.unit}
      disabled={step !== ModifyUnitActivationStep.unit}
      classes='bg-surface border border-border rounded-2xl text-text'
    >
      <div className='grid sm:grid-cols-2 gap-[20px] sm:h-[200px]'>
        <div className='flex flex-col gap-[10px] h-[100%]'>
          <div className='relative grow w-full bg-surface-2 border border-border rounded-2xl flex items-center justify-center p-4'>
            <InfoModalButton
              className='!absolute !top-[10px] !right-[10px] z-[99]'
              iconClasses='text-[1.1rem]'
              subtitle={INFO_ITEM_SERVICE_REQUESTS.existingUnit.subtitle}
              title={INFO_ITEM_SERVICE_REQUESTS.existingUnit.title}
              body={INFO_ITEM_SERVICE_REQUESTS.existingUnit.body}
            />
            <div className='w-full'>
              {(!existingUnitIdVal || existingUnitIdVal?.length === 0) && (
                <ModalButton
                  component={PortalUnitSelectModal}
                  componentProps={{
                    title: 'Active devices',
                    filterDefaults: PORTAL_UNIT_FILER_DEFAULTS_ACTIVE
                  }}
                  confirmCallback={(data: IPortalUnitSearchModel) => setExistingUnit(data)}
                  enterAnimation={modalSlideUpEnterAnimation}
                  leaveAnimation={modalSlideUpLeaveAnimation}
                  classes='relative w-full pb-7 sm:py-4 p-4'
                >
                  <div className='flex flex-col items-center justify-between gap-[10px]'>
                    <div className='flex flex-col items-center justify-center gap-[2px] bg-surface border border-border rounded-xl px-[15px] py-[6px] jost-light text-[0.9rem] leading-[1.1rem] '>
                      <commonIcons.add className='text-[1.1rem]' />
                      Add
                    </div>
                    <p className='jost-light text-[0.9rem] leading-[1.1rem]'>
                      <div className='jost'>No exiting device added.</div>
                      Select an existing device.
                    </p>
                  </div>
                  <FieldErrorMessage
                    classes='absolute left-0 bottom-[5px] sm:bottom-[-5px] !pl-0 w-full text-center'
                    hideWhenNotInError
                    errors={errors?.existingUnitId}
                  />
                </ModalButton>
              )}
              {existingUnitIdVal && existingUnitIdVal?.length > 0 && (
                <div className='w-full bg-surface border border-border rounded-3xl p-4 flex flex-col gap-[15px]'>
                  <div className='flex items-start justify-between'>
                    <div className='flex items-center justify-start gap-[5px]'>
                      <div className='w-[50px] h-[50px] rounded-[25px] p-[5px] bg-surface-2 flex items-center justify-center'>
                        <ImageComponent
                          src={existingUnit?.productImageUrl}
                          alt='Product'
                          imageClasses='max-w-[80%]'
                          errorIconClasses='text-[1.5rem]'
                        />
                      </div>
                      <div className='text-[0.85em] jost-light'>
                        <div className='pl-[3px] uppercase'>{existingUnit?.productName}</div>
                        <div>{valueStringCheck(existingUnit?.siteName, [], '<No site details>')}</div>
                      </div>
                    </div>
                    <ButtonRoundSm type='button' className='bg-surface-2' callback={() => setExistingUnit(undefined)}>
                      <commonIcons.remove />
                    </ButtonRoundSm>
                  </div>
                  <div className='flex flex-col gap-[6px] text-[0.8em] leading-[1.2em] px-2'>
                    <div className='flex'>
                      <span className='bg-surface-2 text-text px-1 rounded-[5px] mr-[2px]'>AccountNo</span>
                      <span className='jost-medium'>
                        <ButtonCopy value={existingUnit?.accountNo} showValue iconClassName='text-[1.1em]' />
                      </span>
                    </div>
                    <div className='flex'>
                      <span className='bg-surface-2 text-text px-1 rounded-[5px] mr-[2px]'>Mobile</span>
                      <span className='jost-medium'>
                        <ButtonCopy value={existingUnit?.mobileNo} showValue iconClassName='text-[1.1em]' />
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='relative flex flex-col items-start justify-center gap-[10px] h-[100%]'>
          <InfoModalButton
            className='!absolute !top-[10px] !right-[10px] z-[99]'
            iconClasses='text-[1.1rem]'
            subtitle={INFO_ITEM_SERVICE_REQUESTS.inactiveUnit.subtitle}
            title={INFO_ITEM_SERVICE_REQUESTS.inactiveUnit.title}
            body={INFO_ITEM_SERVICE_REQUESTS.inactiveUnit.body}
          />
          <div className='relative grow w-full bg-surface-2 border border-border rounded-2xl flex items-center justify-center p-4'>
            {(!unitIdVal || unitIdVal?.length === 0) && (
              <ModalButton
                component={PortalUnitSelectModal}
                componentProps={{
                  title: 'Inactive devices',
                  replacementButtonComponent: (
                    props: IPortalUnitSearchModel,
                    onDismiss: (data?: string | null | undefined | number, role?: string) => void
                  ) => {
                    if (props.unitActivationRequestID) {
                      return (
                        <button
                          type='button'
                          aria-label='Manage device'
                          onClick={(e) => {
                            e?.stopPropagation()
                            navigateNewTab({
                              routeUrl: ROUTE_UNIT_ACTIVATION_DETAILS.buildPath(props.unitActivationRequestID),
                              route: ROUTE_UNIT_ACTIVATION_DETAILS
                            })
                          }}
                        >
                          <div className='flex items-center justify-start rounded-full px-4 py-1 bg-warning text-on-warning'>
                            <div className='jost-medium italic text-[0.85rem] leading-[1rem] text-left rounded-xl py-1'>
                              Device associated with another activation. Delete or archive this activation first.
                            </div>
                            <div className='flex items-center justify-center w-[35px] min-w-[35px] h-[35px] min-h-[35px] rounded-full bg-warning'>
                              <commonIcons.srActivations className='text-[1.5rem]' />
                            </div>
                          </div>
                        </button>
                      )
                    }

                    return undefined
                  },
                  filterDefaults: PORTAL_UNIT_FILER_DEFAULTS_INACTIVE
                }}
                confirmCallback={(data: IPortalUnitSearchModel) =>
                  updateValues(
                    data.unitId,
                    data.serialNo,
                    data.imei,
                    data.productImageUrl,
                    data.productId,
                    data.productName,
                    data.planId,
                    data.planName,
                    data.planUpgradePath
                  )
                }
                classes='relative w-full pb-7 sm:py-4 p-4'
                enterAnimation={modalSlideUpEnterAnimation}
                leaveAnimation={modalSlideUpLeaveAnimation}
              >
                <div className='flex flex-col items-center justify-between gap-[10px]'>
                  <div className='flex flex-col items-center justify-center gap-[2px] bg-surface border border-border rounded-xl px-[15px] py-[6px] jost-light text-[0.9rem] leading-[1.1rem] '>
                    <commonIcons.add className='text-[1.2rem]' />
                    Add
                  </div>
                  <p className='jost-light text-[0.9rem] leading-[1.1rem]'>
                    <div className='jost'>No inactive device added.</div>
                    Select a new inactive device.
                  </p>
                </div>
                <FieldErrorMessage
                  classes='absolute left-0 bottom-[5px] sm:bottom-[-5px] !pl-0 w-full text-center'
                  hideWhenNotInError
                  errors={errors?.unitId}
                />
              </ModalButton>
            )}
            {unitIdVal && unitIdVal?.length > 0 && (
              <div className='w-full bg-surface border border-border rounded-3xl p-4 flex flex-col gap-[15px]'>
                <div className='flex items-start justify-between'>
                  <div className='flex items-center justify-start gap-[5px]'>
                    <div className='w-[50px] h-[50px] rounded-[25px] p-[5px] bg-surface-2 flex items-center justify-center'>
                      <ImageComponent src={productImageUrlVal} alt='Product' imageClasses='max-w-[80%]' errorIconClasses='text-[1.5rem]' />
                    </div>
                    <div className='text-[0.85em] jost-light'>
                      <div className='pl-[3px] uppercase'>{productNameVal}</div>
                    </div>
                  </div>
                  <ButtonRoundSm type='button' className='bg-surface-2' callback={() => clearValues()}>
                    <commonIcons.remove />
                  </ButtonRoundSm>
                </div>
                <div className='flex flex-col gap-[6px] text-[0.8em] leading-[1.2em] px-1'>
                  <div className='flex'>
                    <span className='bg-surface-2 text-text px-1 rounded-[5px] mr-[2px]'>SerialNo</span>
                    <span className='jost-medium'>
                      <ButtonCopy value={serialNoVal} showValue iconClassName='text-[1.1em]' />
                    </span>
                  </div>
                  <div className='flex'>
                    <span className='bg-surface-2 text-text px-1 rounded-[5px] mr-[2px]'>IMEI</span>
                    <span className='jost-medium'>
                      <ButtonCopy value={imeiVal} showValue iconClassName='text-[1.1em]' />
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='flex items-center justify-end gap-[10px] mt-[20px]'>
        <ButtonRoundedSm
          type='submit'
          loading={loading || updating}
          text='Next'
          iconRight
          className='sm:grow-0 action-success'
          icon={<commonIcons.arrowRight />}
        />
      </div>
      <VertSpacerLg />
    </CollapsibleContent>
  )
}
